import React, { Fragment, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import {
  EMPTY_PUSH_CTA,
  PUSH_MAX_CTAS,
  PUSH_TARGET_SCREEN_EXAMPLES,
  PUSH_TARGET_SCREEN_PREFIX,
  notificationValidations,
} from "./notificationConstant";
import FormInput from "../Common/FormInputs/MuiTextField";
import WysiwygEditorField from "../inAppNotificationManagement/WysiwygEditorField";
import Button from "../Common/Button/Button";
import IconButton from "../Common/Button/IconButton";
import Chip from "@mui/material/Chip";
import { useDesignTokens } from "../../theme/useDesignTokens";
import { defaultNotificationImageUploadHandler } from "./emailEditorUtils";
import { fileInputStyle } from "../assetManagement/assetConstants";
import {
  emailCompactFormControlSx,
  emailFieldRowSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "./emailFormStyles";

const messageFieldRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr)" },
  alignItems: { xs: "start", md: "start" },
  gap: 1.5,
  marginBottom: 2,
};

const threeQuarterWidthControlSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: { xs: "100%", md: "75%" },
};

const PushNotification = ({
  messageData = {
    title: "",
    description: "",
    targetScreen: "",
    image: "",
    ctas: [],
  },
  handleMessageData,
  onImageUploadError,
  showCtas = true,
  templateSlot = null,
  templateHint = "",
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);
  const titleMax = notificationValidations?.push?.title?.maxLength || 100;
  const descriptionMax = notificationValidations?.push?.body?.maxLength || 1000;
  const targetScreenMax =
    notificationValidations?.push?.targetScreen?.maxLength || 500;

  const handleTargetScreenChange = (nextValue) => {
    handleMessageData(
      "targetScreen",
      String(nextValue || "").substring(0, targetScreenMax)
    );
  };
  const ctas = Array.isArray(messageData?.ctas) ? messageData.ctas : [];
  const canAddCta = ctas.length < PUSH_MAX_CTAS;
  const imageFileInputRef = useRef(null);
  const [imageUploading, setImageUploading] = useState(false);
  const imageUrl = (messageData?.image || "").trim();

  useEffect(() => {
    if (!imageUrl && imageFileInputRef.current) {
      imageFileInputRef.current.value = "";
    }
  }, [imageUrl]);

  const updateCta = (index, field, value) => {
    handleMessageData(
      "ctas",
      ctas.map((cta, i) => (i === index ? { ...cta, [field]: value } : cta))
    );
  };

  const removeCta = (index) => {
    handleMessageData(
      "ctas",
      ctas.filter((_, i) => i !== index)
    );
  };

  const handleImageFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImageUploading(true);
    try {
      const url = await defaultNotificationImageUploadHandler(file);
      handleMessageData("image", url);
    } catch (err) {
      onImageUploadError?.(err?.message || "Failed to upload image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleClearImage = () => {
    handleMessageData("image", "");
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = "";
    }
  };

  return (
    <Fragment>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Message
      </Typography>
      <Paper sx={emailSectionCardSx}>
        {templateSlot}
        {templateHint ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2, ml: { md: "212px" } }}>
            {templateHint}
          </Typography>
        ) : null}
        <Box sx={emailFieldRowSx}>
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            Title
            <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
              *
            </Typography>
          </Typography>
          <Box sx={controlSx}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="Enter title"
              value={messageData?.title || ""}
              onChange={(e) => {
                handleMessageData("title", e.target.value.substring(0, titleMax));
              }}
              sx={focusOutlineSx}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {(messageData?.title?.length || 0) + "/" + titleMax}
            </Typography>
          </Box>
        </Box>

        <Box sx={messageFieldRowSx}>
          <Typography variant="body2" sx={{ fontWeight: 400, pt: { md: 0.5 } }}>
            Description
            <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
              *
            </Typography>
          </Typography>
          <Box sx={threeQuarterWidthControlSx}>
            <WysiwygEditorField
              value={messageData?.description || ""}
              onChange={(html) => handleMessageData("description", html)}
              height={180}
              hideLabel
              maxLength={descriptionMax}
              helperText={`${
                (messageData?.description || "").length
              }/${descriptionMax}`}
            />
          </Box>
        </Box>

        <Box sx={{ ...messageFieldRowSx, alignItems: { xs: "start", md: "start" } }}>
          <Typography variant="body2" sx={{ fontWeight: 400, pt: { md: 0.5 } }}>
            Target Screen
          </Typography>
          <Box sx={threeQuarterWidthControlSx}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              name="targetScreen"
              placeholder=""
              value={messageData?.targetScreen || ""}
              onChange={(e) => handleTargetScreenChange(e.target.value)}
              sx={focusOutlineSx}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75, fontFamily: "monospace" }}
            >
              Example: {PUSH_TARGET_SCREEN_PREFIX}dashboard
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                mt: 1,
              }}
            >
              {PUSH_TARGET_SCREEN_EXAMPLES.map((example) => (
                <Chip
                  key={example.value}
                  label={example.label}
                  size="small"
                  variant="outlined"
                  clickable
                  title={example.hint ? `${example.hint}\n${example.value}` : example.value}
                  onClick={() => handleTargetScreenChange(example.value)}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={emailFieldRowSx}>
          <Typography variant="body2" sx={{ fontWeight: 400, pt: { md: 0.5 } }}>
            Image URL
          </Typography>
          <Box sx={controlSx}>
            <input
              ref={imageFileInputRef}
              type="file"
              accept="image/*"
              disabled={imageUploading || Boolean(imageUrl)}
              onChange={handleImageFileChange}
              style={fileInputStyle}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75 }}
            >
              {imageUploading
                ? "Uploading image..."
                : "Select an image to set Image URL."}
            </Typography>

            {imageUrl && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="Uploaded notification"
                    sx={{
                      width: "100%",
                      maxWidth: 220,
                      maxHeight: 140,
                      objectFit: "contain",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  />
                </Box>
                <IconButton
                  variant="danger"
                  size="small"
                  tooltip="Remove image"
                  icon={<DeleteIcon fontSize="small" />}
                  onClick={handleClearImage}
                  disabled={imageUploading}
                  aria-label="Remove uploaded image"
                  sx={{ width: 40, height: 40 }}
                />
              </Box>
            )}
          </Box>
        </Box>

      {showCtas ? (
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          pt: 2,
          mt: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: ctas.length ? 1.5 : 1,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            CTA
          </Typography>
          {canAddCta && (
            <Button
              variant="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleMessageData("ctas", [...ctas, { ...EMPTY_PUSH_CTA }])}
            >
              Add CTA
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: ctas.length ? 1.5 : 0 }}>
          Optional — up to {PUSH_MAX_CTAS} CTAs ({ctas.length}/{PUSH_MAX_CTAS})
        </Typography>

        {ctas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No CTAs added yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {ctas.map((cta, index) => (
              <Box
                key={`push-cta-${index}`}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", sm: 180 },
                    minWidth: 0,
                  }}
                >
                  <FormInput
                    size="small"
                    fullWidth
                    placeholder="Text"
                    value={cta.label || ""}
                    onChange={(e) =>
                      updateCta(
                        index,
                        "label",
                        e.target.value.substring(
                          0,
                          notificationValidations.push.ctaLabel.maxLength
                        )
                      )
                    }
                    sx={focusOutlineSx}
                  />
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", sm: 280 },
                    minWidth: 0,
                  }}
                >
                  <FormInput
                    size="small"
                    fullWidth
                    placeholder="Path after miraie:// (e.g. dashboard or webview?externalUrl=https://...)"
                    value={cta.target || ""}
                    onChange={(e) =>
                      updateCta(
                        index,
                        "target",
                        e.target.value.substring(
                          0,
                          notificationValidations.push.ctaTarget.maxLength
                        )
                      )
                    }
                    sx={focusOutlineSx}
                  />
                </Box>
                <IconButton
                  variant="danger"
                  size="small"
                  tooltip="Remove CTA"
                  icon={<DeleteIcon fontSize="small" />}
                  onClick={() => removeCta(index)}
                  aria-label={`Remove CTA ${index + 1}`}
                  sx={{ width: 40, height: 40, flexShrink: 0 }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
      ) : null}
      </Paper>
    </Fragment>
  );
};

export default PushNotification;
